import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography,
  Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, IconButton, Chip, Grid, Alert, Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HistoryEdu,
  Height,
  ConveyorBelt
} from '@mui/icons-material';
import { getRulesByContext, createRule, updateRule, deleteRule } from '../../services/ImpactAPIs'; // update path
import PageHeader from '../../components/headers/Header';


const LeaveImpactRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    leaveType: '',
    impactType: 'DEDUCTION',
    percentage: 0,
    fixedAmount: 0,
    maxDaysPerMonth: 0,
    context: 'leave',
    description: '',
    isActive: true
  });

  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
    MATERNITY_LEAVE: 'Maternity Leave',
  };

  // Fetch rules on component mount
  useEffect(() => {
    const fetchRules = async () => {
      try {
        const data = await getRulesByContext('leave');
        console.log('Fetched rules:', data);
        setRules(data);
      } catch (error) {
        console.error('Error fetching rules:', error);
        setSnackbar({ open: true, message: 'Failed to fetch rules', severity: 'error' });
      }
    };

    fetchRules();
  }, []);



  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData(rule);
    } else {
      setEditingRule(null);
      setFormData({
        leaveType: '',
        impactType: 'DEDUCTION',
        percentage: 0,
        fixedAmount: 0,
        maxDaysPerMonth: 0,
        description: '',
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingRule(null);
  };

  const handleSave = async () => {
    try {
      const updatedFormData = {
        ...formData,
        context: 'leave'
      };

      if (editingRule) {
        await updateRule(editingRule.id, updatedFormData);
        setSnackbar({ open: true, message: 'Leave impact rule updated successfully!', severity: 'success' });
      } else {
        await createRule(updatedFormData);
        setSnackbar({ open: true, message: 'Leave impact rule created successfully!', severity: 'success' });
      }
      // Refresh rules after save
      const data = await getRulesByContext('leave');
      setRules(data);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving rule:', error);
      setSnackbar({ open: true, message: 'Failed to save rule', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRule(id);
      setSnackbar({ open: true, message: 'Leave impact rule deleted successfully!', severity: 'success' });

    } catch (error) {
      console.error('Error deleting rule:', error);
      setSnackbar({ open: true, message: 'Failed to delete rule', severity: 'error' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getImpactDisplay = (rule) => {
    if (rule.impactType === 'NO_IMPACT') return 'No Impact';
    if (rule.impactType === 'DEDUCTION') {
      if (rule.impactFactor > 0) return `${rule.impactFactor}% Deduction`;
      // if (rule.fixedAmount > 0) return `$${rule.fixedAmount} Fixed Deduction`;
      return 'Salary Deduction';
    }
    if (rule.impactType === 'BONUS') return 'Bonus';

    return 'Custom Impact';
  };


  const getImpactColor = (impactType) => {
    switch (impactType) {
      case 'NO_IMPACT': return 'success';
      case 'DEDUCTION': return 'error';
      case 'BONUS': return 'info';
      default: return 'default';
    }
  };


  return (
    <Box sx={{ p: 3, padding: 0, margin: 0 }}>
      <PageHeader
        title="Leave Impact Rules"
        subtitle="Configure how different types of leaves affect employee salaries"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            Add New Rule
          </Button>
        }
      />

      {/* Rules Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Leave Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Impact</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Max Days/Month</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {/* {rule.leaveType.replace('_', ' ')} */}
                        {leaveTypeLabels[rule.leaveType] || rule.leaveType.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getImpactDisplay(rule)}
                        //label={rule.impactType.replace('_', ' ')}
                        color={getImpactColor(rule.impactType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rule.maxDaysPerMonth || 'No limit'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rule.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.isActive ? 'Active' : 'Inactive'}
                        color={rule.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(rule)}
                        sx={{ color: '#1976d2', mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(rule.id)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" color="primary" fontWeight="600">
            {editingRule ? 'Edit Leave Impact Rule' : 'Create New Leave Impact Rule'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }} >
            <Grid item xs={12} md={6} style={{ minWidth: 120 }}>
              <FormControl fullWidth >
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={formData.leaveType}
                  label="Leave Type"
                  onChange={(e) => handleInputChange('leaveType', e.target.value)}
                >
                  <MenuItem value="SICK_LEAVE">Sick Leave</MenuItem>
                  <MenuItem value="ANNUAL_LEAVE">Annual Leave</MenuItem>
                  {/* <MenuItem value="UNPAID_LEAVE">Unpaid Leave</MenuItem> */}
                  <MenuItem value="MATERNITY_LEAVE">Maternity Leave</MenuItem>
                  {/* <MenuItem value="PATERNITY_LEAVE">Paternity Leave</MenuItem> */}
                  <MenuItem value="UNPAID_LEAVE">Personal Leave</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Impact Type</InputLabel>
                <Select
                  value={formData.impactType}
                  label="Impact Type"
                  onChange={(e) => handleInputChange('impactType', e.target.value)}
                >
                  <MenuItem value="NO_IMPACT">No Impact</MenuItem>
                  <MenuItem value="DEDUCTION">Salary Deduction</MenuItem>
                  <MenuItem value="BONUS">Bonus</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.impactType === 'DEDUCTION' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Percentage (%)"
                    type="number"
                    value={formData.impactFactor}
                    onChange={(e) => handleInputChange('impactFactor', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>

                {/* <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fixed Amount ($)"
                    type="number"
                    value={formData.fixedAmount}
                    onChange={(e) => handleInputChange('fixedAmount', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                  />
                </Grid> */}
              </>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Days Per Month"
                type="number"
                value={formData.maxDaysPerMonth}
                onChange={(e) => handleInputChange('maxDaysPerMonth', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
                helperText="0 = No limit"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive}
                  label="Status"
                  onChange={(e) => handleInputChange('isActive', e.target.value)}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} >
              <TextField
                fullWidth
                label="Description"
                //multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe when and how this rule applies..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseDialog}
            startIcon={<CancelIcon />}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ textTransform: 'none', ml: 2 }}
            disabled={!formData.leaveType}
          >
            {editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveImpactRulesManager;


// Mock data for demonstration
//   useEffect(() => {
//     setRules([
//       {
//         id: 1,
//         leaveType: 'SICK_LEAVE',
//         impactType: 'DEDUCTION',
//         percentage: 100,
//         fixedAmount: 0,
//         maxDaysPerMonth: 5,
//         description: 'Full salary deduction for sick leave exceeding 5 days',
//         isActive: true
//       },
//       {
//         id: 2,
//         leaveType: 'ANNUAL_LEAVE',
//         impactType: 'NO_IMPACT',
//         percentage: 0,
//         fixedAmount: 0,
//         maxDaysPerMonth: 0,
//         description: 'Annual leave has no salary impact',
//         isActive: true
//       },
//       {
//         id: 3,
//         leaveType: 'UNPAID_LEAVE',
//         impactType: 'DEDUCTION',
//         percentage: 100,
//         fixedAmount: 0,
//         maxDaysPerMonth: 0,
//         description: 'Full salary deduction for unpaid leave',
//         isActive: true
//       }
//     ]);
//   }, []);